/**
 * Stack Your Story — Bundle Builder Engine
 * Handles state, pricing, mobile drawer, desktop summary, bottom sheet
 * customization flow (variants, engraving, photo upload), and checkout.
 * Vanilla JS only. No jQuery.
 */
(function () {
  'use strict';

  /* ===== DISCOUNT TIERS ===== */
  var DISCOUNT_TIERS = { 2: 0.10, 3: 0.15, 4: 0.25 };
  var MAX_DISCOUNT = 0.25;

  /* ===== CUSTOMIZATION TAG MAP ===== */
  var ENGRAVE_TAGS = ['custom-engravable-new', 'customornament'];
  var PHOTO_TAGS = ['photo upload', 'custom necklace', 'pet-portrait-charm', 'filagree-necklace', 'circle-photo-earring'];
  var LINE_TAGS = ['line1', 'line2', 'line3', 'line4'];

  /* ===== ENGRAVING FONTS (matches PDP FONT_PROPERTIES + FONTS_BY_TAG) ===== */
  var ALL_FONTS = {
    F1: { label: 'Nickainley Normal', code: 'F1', css: "'Nickainley Normal', cursive" },
    F2: { label: 'Kumbh Sans',       code: 'F2', css: "'Kumbh Sans', sans-serif" },
    F3: { label: 'Dayrom',           code: 'F3', css: "'Dayrom', sans-serif" },
    F4: { label: 'Holland Land',     code: 'F4', css: "'Holland Land', cursive" },
    F5: { label: 'Magnum',           code: 'F5', css: "'Magnum', sans-serif" },
    F6: { label: 'Grafolita',        code: 'F6', css: "'Grafolita', cursive" },
    F8: { label: 'Alga',             code: 'F8', css: "'Alga', sans-serif" }
  };

  // Default 5 fonts used by most engrave types (PDP FONTS_BY_TAG default)
  var DEFAULT_FONT_CODES = ['F8', 'F2', 'F5', 'F1', 'F4'];
  var DEFAULT_FONT_CODE = 'F8'; // Alga — PDP defaults to Alga when Dayrom not in list

  // Font overrides per engrave type (only list exceptions)
  var FONTS_BY_ENGRAVE_TYPE = {
    'ENGRAVABLE_CIRCLE_PHOTO_NECKLACE': ['F2'],
    'ENGRAVABLE_MEMORIAL_PHOTO_ORNAMENT': ['F8', 'F2', 'F1']   // WF-H26-2202: Alga, Kumbh Sans, Nickainley only
  };

  /* ===== ENGRAVE TYPE TAG DETECTION (matches PDP line 4373 + FONTS_BY_TAG) ===== */
  var ENGRAVE_TAG_TO_TYPE = [
    ['engravable-heart-charm', 'HEART_CHARM'],
    ['engravable-circle-charm', 'CIRCLE_CHARM'],
    ['pave-circle-engravable-charm', 'PAVE_CIRCLE_ENGRAVABLE_CHARM'],
    ['engravable-circle-photo-necklace', 'ENGRAVABLE_CIRCLE_PHOTO_NECKLACE'],
    ['engravable-bar-charm', 'ENGRAVABLE_BAR_CHARM'],
    ['engravable-bar-tennis-bracelet', 'ENGRAVABLE_BAR_TENNIS_BRACELET'],
    ['engravable-bar-paperclip-bracelet', 'ENGRAVABLE_BAR_PAPERCLIP_BRACELET'],
    ['engravable-bar-leather-bracelet', 'ENGRAVABLE_BAR_LEATHER_BRACELET'],
    ['engravable-curb-chain-id-bracelet', 'ENGRAVABLE_CURB_CHAIN_ID_BRACELET'],
    ['engravable-layered-dog-tag-necklace', 'ENGRAVABLE_LAYERED_DOG_TAG_NECKLACE'],
    ['engravable-cross-necklace', 'ENGRAVABLE_CROSS_NECKLACE'],
    ['engravable-dog-tag-locket-necklace', 'ENGRAVABLE_DOG_TAG_LOCKET_NECKLACE'],
    ['engravable-bar-paperclip-necklace', 'ENGRAVABLE_BAR_PAPERCLIP_NECKLACE'],
    ['engravable-bar-circle-photo-bracelet', 'ENGRAVABLE_BAR_CIRCLE_PHOTO_BRACELET'],
    ['engravable-circle-photo-bookmark', 'ENGRAVABLE_CIRCLE_PHOTO_BOOKMARK'],
    ['engravable-heart-photo-bookmark', 'ENGRAVABLE_HEART_PHOTO_BOOKMARK'],
    ['engravable-bar-ring', 'ENGRAVABLE_BAR_RING'],
    ['engravable-heart-locket-keychain', 'ENGRAVABLE_HEART_LOCKET_KEYCHAIN'],
    ['circle-photo-square-keychain', 'CIRCLE_PHOTO_SQUARE_KEYCHAIN'],
    ['engravable-circle-photo-lariat-necklace', 'ENGRAVABLE_CIRCLE_PHOTO_LARIAT_NECKLACE'],
    ['engravable-id-bracelet', 'ENGRAVABLE_ID_BRACELET'],
    ['engravable-pendant-necklace', 'ENGRAVABLE_PENDANT_NECKLACE'],
    ['engravable-snowflake-ornament', 'ENGRAVABLE_SNOWFLAKE_ORNAMENT'],
    ['engravable-i-love-my-dog-charm', 'ENGRAVABLE_I_LOVE_MY_DOG_CHARM'],
    ['engravable-sport-star-ornament', 'ENGRAVABLE_SPORT_STAR_ORNAMENT'],
    ['engravable-paw-print-heart-charm', 'ENGRAVABLE_PAW_PRINT_HEART_CHARM'],
    ['engravable-memorial-photo-ornament', 'ENGRAVABLE_MEMORIAL_PHOTO_ORNAMENT'],
    ['heart-birthstone-bracelet-engravable', 'HEART_BIRTHSTONE_BRACELET'],
    ['engravable-square-necklace', 'SQUARE_NECKLACE'],
    ['spin-necklace', 'SPIN_NECKLACE'],
    ['heart-photo-keychain', 'HEART_PHOTO_KEYCHAIN'],
    ['circle-quadrant-necklace', 'CIRCLE_QUADRANT_NECKLACE'],
    ['circle-photo-keychain', 'CIRCLE_PHOTO_KEYCHAIN'],
    ['circle-bracelet', 'CIRCLE_BRACELET'],
    ['bar-bracelet', 'BAR_BRACELET'],
    ['oval-vertical-locket', 'OVAL_VERTICAL_LOCKET'],
    ['oval-locket-ring', 'OVAL_LOCKET_RING'],
    ['engravable-round-halo-locket-ring', 'ROUND_HALO_LOCKET_RING'],
    ['engravable-heart-halo-locket-ring', 'HEART_HALO_LOCKET_RING'],
    ['oval-locket-bracelet', 'OVAL_LOCKET_BRACELET'],
    ['heart-locket-necklace', 'HEART_LOCKET_NECKLACE'],
    ['heart-locket-ring', 'HEART_LOCKET_RING'],
    ['heart-locket-bracelet', 'HEART_LOCKET_BRACELET'],
    ['flip-locket', 'FLIP_LOCKET']
  ];

  /* ===== LINE CONFIG PER ENGRAVE TYPE (matches PDP containersByType) ===== */
  var CONTAINERS_BY_TYPE = {
    'HEART_CHARM':                          [{ backend: 'Line 1', maxLength: 3 }],
    'CIRCLE_CHARM':                         [{ backend: 'Line 1', maxLength: 3 }],
    'PAVE_CIRCLE_ENGRAVABLE_CHARM':         [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_CIRCLE_PHOTO_NECKLACE':     [{ backend: 'Line 1', maxLength: 9 }, { backend: 'Line 2', maxLength: 9 }, { backend: 'Line 3', maxLength: 9 }, { backend: 'Line 4', maxLength: 9 }],
    'ENGRAVABLE_BAR_CHARM':                 [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_BAR_TENNIS_BRACELET':       [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_BAR_PAPERCLIP_BRACELET':    [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_BAR_LEATHER_BRACELET':      [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_CURB_CHAIN_ID_BRACELET':    [{ backend: 'Line 1', maxLength: 16 }],
    'ENGRAVABLE_LAYERED_DOG_TAG_NECKLACE':  [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_CROSS_NECKLACE':            [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_DOG_TAG_LOCKET_NECKLACE':   [{ backend: 'Line 1', maxLength: 3 }],
    'ENGRAVABLE_BAR_PAPERCLIP_NECKLACE':    [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_BAR_CIRCLE_PHOTO_BRACELET': [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_CIRCLE_PHOTO_BOOKMARK':     [{ backend: 'Line 1', maxLength: 40 }],
    'ENGRAVABLE_HEART_PHOTO_BOOKMARK':      [{ backend: 'Line 1', maxLength: 40 }],
    'ENGRAVABLE_BAR_RING':                  [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_HEART_LOCKET_KEYCHAIN':     [{ backend: 'Line 1', maxLength: 3 }],
    'CIRCLE_PHOTO_SQUARE_KEYCHAIN':         [{ backend: 'Line 1', maxLength: 3 }],
    'ENGRAVABLE_CIRCLE_PHOTO_LARIAT_NECKLACE': [{ backend: 'Line 1', maxLength: 9 }],
    'ENGRAVABLE_ID_BRACELET':               [{ backend: 'Line 1', maxLength: 15 }],
    'ENGRAVABLE_PENDANT_NECKLACE':          [{ backend: 'Line 1', maxLength: 15 }],
    'ENGRAVABLE_SNOWFLAKE_ORNAMENT':        [{ backend: 'Line 1', maxLength: 12 }],
    'ENGRAVABLE_I_LOVE_MY_DOG_CHARM':       [{ backend: 'Line 1', maxLength: 10 }],
    'ENGRAVABLE_SPORT_STAR_ORNAMENT':       [{ backend: 'Line 1', maxLength: 9 }, { backend: 'Line 2', maxLength: 4 }],
    'ENGRAVABLE_PAW_PRINT_HEART_CHARM':     [{ backend: 'Line 1', maxLength: 8 }],
    'ENGRAVABLE_MEMORIAL_PHOTO_ORNAMENT':   [{ backend: 'Line 1', maxLength: 25 }, { backend: 'Line 2', maxLength: 25 }],
    'HEART_BIRTHSTONE_BRACELET':            [{ backend: 'Line 1', maxLength: 9 }],
    'SQUARE_NECKLACE':                      [{ backend: 'Line 1', maxLength: 3 }],
    'HEART_PHOTO_KEYCHAIN':                 [{ backend: 'Line 1', maxLength: 3 }],
    'CIRCLE_BRACELET':                      [{ backend: 'Line 1', maxLength: 3 }],
    'BAR_BRACELET':                         [{ backend: 'Line 1', maxLength: 10 }],
    'OVAL_VERTICAL_LOCKET':                 [{ backend: 'Line 1', maxLength: 3 }],
    'OVAL_LOCKET_RING':                     [{ backend: 'Line 1', maxLength: 3 }],
    'ROUND_HALO_LOCKET_RING':               [{ backend: 'Line 1', maxLength: 3 }],
    'HEART_HALO_LOCKET_RING':               [{ backend: 'Line 1', maxLength: 3 }],
    'OVAL_LOCKET_BRACELET':                 [{ backend: 'Line 1', maxLength: 3 }],
    'HEART_LOCKET_NECKLACE':                [{ backend: 'Line 1', maxLength: 3 }],
    'HEART_LOCKET_RING':                    [{ backend: 'Line 1', maxLength: 3 }],
    'HEART_LOCKET_BRACELET':                [{ backend: 'Line 1', maxLength: 3 }],
    'FLIP_LOCKET':                          [{ backend: 'Line 1', maxLength: 3 }],
    'SPIN_NECKLACE':                        [{ backend: 'Line 1 Front', maxLength: 10 }, { backend: 'Line 2 Front', maxLength: 10 }, { backend: 'Line 3 Front', maxLength: 10 }]
  };

  function getFontCssByCode(code) {
    return ALL_FONTS[code] ? ALL_FONTS[code].css : ALL_FONTS.F8.css;
  }

  function getFontsForProduct(tags) {
    var eType = getDetailedEngraveType(tags);
    var codes = (eType && FONTS_BY_ENGRAVE_TYPE[eType]) || DEFAULT_FONT_CODES;
    var fonts = [];
    for (var i = 0; i < codes.length; i++) {
      if (ALL_FONTS[codes[i]]) fonts.push(ALL_FONTS[codes[i]]);
    }
    return fonts;
  }

  function getDefaultFontCode(tags) {
    var fonts = getFontsForProduct(tags);
    // If default (F8) is in the list, use it; otherwise use first available
    for (var i = 0; i < fonts.length; i++) {
      if (fonts[i].code === DEFAULT_FONT_CODE) return DEFAULT_FONT_CODE;
    }
    return fonts.length > 0 ? fonts[0].code : DEFAULT_FONT_CODE;
  }

  /* ===== SWATCH CDN (matches PDP color-swatch-picker.liquid logic) ===== */
  var SWATCH_CDN = 'https://cdn.shopify.com/s/files/1/0586/0117/7256/files/';

  /* ===== STATE ===== */
  var stackItems = []; // Extended: { variantId, productId, title, price, image, handle, properties: {}, optionsSummary: '' }

  /* ===== SHEET STATE ===== */
  var sheetOpen = false;
  var sheetCard = null; // DOM reference to the card that opened the sheet
  var sheetProductId = null;
  var sheetSelectedOptions = {}; // { 'Color': 'Gold', 'Size': '7' }
  var sheetProperties = {}; // { '_FONT': 'Script', '_TEXT1': 'Hello', ... }
  var sheetPhotoUrl = '';

  /* ===== HELPERS ===== */
  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\.00$/, '');
  }

  function getItemCount() {
    return stackItems.length;
  }

  function getSubtotal() {
    var sum = 0;
    for (var i = 0; i < stackItems.length; i++) {
      sum += stackItems[i].price;
    }
    return sum;
  }

  function getDiscountPercent() {
    var count = getItemCount();
    if (count >= 4) return MAX_DISCOUNT;
    return DISCOUNT_TIERS[count] || 0;
  }

  function getDiscountAmount() {
    return Math.round(getSubtotal() * getDiscountPercent());
  }

  function getDiscountedTotal() {
    return getSubtotal() - getDiscountAmount();
  }

  function getNextTierMessage() {
    var count = getItemCount();
    if (count === 0) return 'Add 2 pieces to unlock 10% off!';
    if (count === 1) return 'Add 1 more piece to unlock 10% off!';
    if (count === 2) return 'Add 1 more piece to unlock 15% off!';
    if (count === 3) return 'Add 1 more piece to unlock 25% off!';
    return 'You\u2019re saving 25% \u2014 maximum discount!';
  }

  function findItemByProduct(productId) {
    for (var i = 0; i < stackItems.length; i++) {
      if (String(stackItems[i].productId) === String(productId)) return i;
    }
    return -1;
  }

  function findItemIndex(variantId) {
    for (var i = 0; i < stackItems.length; i++) {
      if (String(stackItems[i].variantId) === String(variantId)) return i;
    }
    return -1;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function getProduct(productId) {
    return (window.__stackProducts && window.__stackProducts[productId]) || null;
  }

  function hasTag(tags, tag) {
    if (!tags) return false;
    for (var i = 0; i < tags.length; i++) {
      if (tags[i].toLowerCase() === tag.toLowerCase()) return true;
    }
    return false;
  }

  function hasAnyTag(tags, tagList) {
    for (var i = 0; i < tagList.length; i++) {
      if (hasTag(tags, tagList[i])) return true;
    }
    return false;
  }

  function getSwatchStyle(product, optionName, value) {
    var iconName = value.replace(/ \/ /g, '/').replace(/ /g, '_').replace(/'/g, '');
    var optLower = (optionName || '').toLowerCase();
    var tags = product.tags || [];
    var image = '';
    var fallback = '';

    if (hasTag(tags, 'watercolor-portrait-charm')) {
      image = SWATCH_CDN + 'Icon-' + iconName + '_Watercolor.png';
    } else if (hasTag(tags, 'color-swatch')) {
      image = SWATCH_CDN + 'SwatchIcon-' + iconName + '.png';
    } else if (hasTag(tags, 'charm builder')) {
      if (hasTag(tags, 'team spirit builder')) {
        image = SWATCH_CDN + 'TeamSpirit-' + iconName.replace(/\//g, '-') + '.svg';
      } else {
        image = SWATCH_CDN + 'Icon-' + iconName + '.png';
      }
    } else if (hasTag(tags, 'team spirit builder')) {
      image = SWATCH_CDN + 'TeamSpirit-' + iconName.replace(/\//g, '-') + '.svg';
    } else if (hasTag(tags, 'stack-photo-bracelet')) {
      image = SWATCH_CDN + 'Stack-' + iconName.replace(/\//g, '-') + '.png';
    } else if (hasTag(tags, 'personalized-tennis-bracelet')) {
      if (iconName === 'Gold') { fallback = '#eee8aa'; }
      else if (iconName === 'Silver') { fallback = '#e6e6e6'; }
      else if (iconName === 'Rose_Gold') { image = SWATCH_CDN + 'Icon-' + iconName + '.png'; }
      else { image = SWATCH_CDN + 'Icon-' + iconName + '.svg'; fallback = 'white'; }
    } else if (hasTag(tags, 'circle-photo-bracelet')) {
      image = SWATCH_CDN + iconName + '.png';
    } else {
      // Default: birthstone/color get icon SVGs, metal gets hex fallback
      if (optLower === 'birthstone' || (optLower === 'color' || optLower === 'colour')) {
        image = SWATCH_CDN + 'Icon-' + iconName + '.svg';
        fallback = 'white';
      }
      if (optLower === 'metal' || optLower === 'metal color') {
        if (iconName === 'Gold') { image = ''; fallback = '#eee8aa'; }
        else if (iconName === 'Silver') { image = ''; fallback = '#e6e6e6'; }
        else { image = SWATCH_CDN + 'Icon-' + iconName + '.svg'; fallback = 'white'; }
      }
    }

    return { image: image, fallback: fallback };
  }

  function isColorOption(name) {
    var n = (name || '').toLowerCase();
    return n === 'color' || n === 'colour' || n === 'metal' || n === 'metal color' || n === 'metal colour' || n === 'birthstone' || n === 'sign';
  }

  /* ===== DETECTION ===== */
  function needsSheet(card) {
    return card.getAttribute('data-has-variants') === 'true' ||
           card.getAttribute('data-has-customization') === 'true';
  }

  /* ===== ADD / REMOVE ITEMS ===== */
  function addItem(data) {
    // Deduplicate by productId (one product per stack)
    var existingIdx = findItemByProduct(data.productId);
    if (existingIdx !== -1) {
      // Replace existing entry for this product
      stackItems[existingIdx] = {
        variantId: String(data.variantId),
        productId: String(data.productId),
        title: data.title,
        price: data.price,
        image: data.image,
        handle: data.handle,
        properties: data.properties || {},
        optionsSummary: data.optionsSummary || ''
      };
    } else {
      stackItems.push({
        variantId: String(data.variantId),
        productId: String(data.productId),
        title: data.title,
        price: data.price,
        image: data.image,
        handle: data.handle,
        properties: data.properties || {},
        optionsSummary: data.optionsSummary || ''
      });
    }
    onStateChange();
  }

  function removeItemByProduct(productId) {
    var idx = findItemByProduct(productId);
    if (idx === -1) return;
    stackItems.splice(idx, 1);
    onStateChange();
  }

  function removeItem(variantId) {
    var idx = findItemIndex(variantId);
    if (idx === -1) return;
    stackItems.splice(idx, 1);
    onStateChange();
  }

  /* ===== CARD CLICK HANDLER ===== */
  function handleCardClick(card) {
    var productId = card.getAttribute('data-product-id');
    var idx = findItemByProduct(productId);

    if (idx !== -1) {
      // Already selected — remove from stack
      removeItemByProduct(productId);
      setCardDeselected(card);
      return;
    }

    if (needsSheet(card)) {
      openSheet(card);
    } else {
      // Simple product — add directly with bounce
      addItem({
        variantId: card.getAttribute('data-variant-id'),
        productId: productId,
        title: card.getAttribute('data-product-title'),
        price: parseInt(card.getAttribute('data-price'), 10),
        image: card.getAttribute('data-product-image'),
        handle: card.getAttribute('data-product-handle')
      });
      setCardSelected(card);
      // Bounce animation
      card.classList.remove('stack-card--bounce');
      void card.offsetHeight;
      card.classList.add('stack-card--bounce');
    }
  }

  /* ===== CARD VISUAL FEEDBACK ===== */
  function setCardSelected(card) {
    card.classList.add('stack-card--selected');
    var btn = card.querySelector('.stack-card__add-btn');
    if (btn) {
      btn.textContent = 'Added \u2713';
      btn.setAttribute('aria-pressed', 'true');
    }
  }

  function setCardDeselected(card) {
    card.classList.remove('stack-card--selected');
    var btn = card.querySelector('.stack-card__add-btn');
    if (btn) {
      btn.textContent = 'Add to Stack';
      btn.setAttribute('aria-pressed', 'false');
    }
  }

  function syncAllCards() {
    var cards = document.querySelectorAll('.stack-card');
    for (var i = 0; i < cards.length; i++) {
      var pid = cards[i].getAttribute('data-product-id');
      if (findItemByProduct(pid) !== -1) {
        setCardSelected(cards[i]);
      } else {
        setCardDeselected(cards[i]);
      }
    }
  }

  /* ===== BOTTOM SHEET ===== */

  function openSheet(card) {
    var productId = card.getAttribute('data-product-id');
    var product = getProduct(productId);
    if (!product) {
      // Fallback: add as simple product directly (no sheet)
      addItem({
        variantId: card.getAttribute('data-variant-id'),
        productId: productId,
        title: card.getAttribute('data-product-title'),
        price: parseInt(card.getAttribute('data-price'), 10),
        image: card.getAttribute('data-product-image'),
        handle: card.getAttribute('data-product-handle')
      });
      setCardSelected(card);
      return;
    }

    sheetCard = card;
    sheetProductId = productId;
    sheetSelectedOptions = {};
    sheetProperties = {};
    sheetPhotoUrl = '';

    // Pre-select first available value for each option
    for (var i = 0; i < product.options.length; i++) {
      var opt = product.options[i];
      if (opt.values && opt.values.length > 0) {
        sheetSelectedOptions[opt.name] = opt.values[0];
      }
    }

    renderSheetContent(product);

    var sheet = document.getElementById('stack-sheet');
    var overlay = document.getElementById('stack-sheet-overlay');
    if (sheet) sheet.classList.add('stack-sheet--open');
    if (overlay) overlay.classList.add('stack-sheet__overlay--visible');
    document.body.style.overflow = 'hidden';
    sheetOpen = true;
  }

  function closeSheet() {
    var sheet = document.getElementById('stack-sheet');
    var overlay = document.getElementById('stack-sheet-overlay');
    if (sheet) sheet.classList.remove('stack-sheet--open');
    if (overlay) overlay.classList.remove('stack-sheet__overlay--visible');
    document.body.style.overflow = '';
    sheetOpen = false;
    sheetCard = null;
    sheetProductId = null;
  }

  /* ===== VARIANT SELECTION ===== */

  function findVariant(product, selectedOptions) {
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var match = true;
      for (var j = 0; j < product.options.length; j++) {
        var optName = product.options[j].name;
        var optPos = product.options[j].position;
        var selectedVal = selectedOptions[optName];
        var variantVal = optPos === 1 ? v.option1 : (optPos === 2 ? v.option2 : v.option3);
        if (selectedVal && variantVal !== selectedVal) {
          match = false;
          break;
        }
      }
      if (match) return v;
    }
    return null;
  }

  function getSelectedVariant(product) {
    return findVariant(product, sheetSelectedOptions);
  }

  function buildOptionsSummary(product) {
    var parts = [];
    for (var i = 0; i < product.options.length; i++) {
      var val = sheetSelectedOptions[product.options[i].name];
      if (val) parts.push(val);
    }
    return parts.join(' / ');
  }

  /* ===== ENGRAVING DETECTION ===== */

  function getDetailedEngraveType(tags) {
    // 1. Check explicit tag → type mappings first
    for (var i = 0; i < ENGRAVE_TAG_TO_TYPE.length; i++) {
      if (hasTag(tags, ENGRAVE_TAG_TO_TYPE[i][0])) return ENGRAVE_TAG_TO_TYPE[i][1];
    }
    // 2. Auto-derive: scan tags for patterns that match CONTAINERS_BY_TYPE keys
    //    e.g. tag "engravable-square-necklace" → "SQUARE_NECKLACE"
    if (!tags) return null;
    for (var j = 0; j < tags.length; j++) {
      var tag = tags[j].toLowerCase();
      // Strip common prefixes and convert to type key
      var stripped = tag.replace(/^engravable-/, '');
      var typeKey = stripped.toUpperCase().replace(/-/g, '_');
      if (CONTAINERS_BY_TYPE[typeKey]) return typeKey;
    }
    return null;
  }

  function getEngravingType(tags) {
    if (hasTag(tags, 'custom-engravable-new')) return 'standard';
    if (hasTag(tags, 'customornament')) return 'ornament';
    return null;
  }

  function getLineCount(tags) {
    var count = 0;
    for (var i = 1; i <= 4; i++) {
      if (hasTag(tags, 'line' + i)) count = i;
    }
    return count;
  }

  function getEngraveLines(tags) {
    // 1. Check for detailed engrave type with specific line config
    var detailedType = getDetailedEngraveType(tags);
    if (detailedType && CONTAINERS_BY_TYPE[detailedType]) {
      return CONTAINERS_BY_TYPE[detailedType];
    }
    // 2. Fall back to line tag detection
    var lineCount = getLineCount(tags);
    var engraveType = getEngravingType(tags);
    if (lineCount > 0) {
      var lines = [];
      for (var i = 1; i <= lineCount; i++) {
        lines.push({ backend: 'Line ' + i, maxLength: 16 });
      }
      return lines;
    }
    // 3. Default line counts for generic types
    if (engraveType === 'ornament') {
      return [
        { backend: 'Line 1', maxLength: 16 },
        { backend: 'Line 2', maxLength: 16 },
        { backend: 'Line 3', maxLength: 16 }
      ];
    }
    if (engraveType === 'standard') {
      return [
        { backend: 'Line 1', maxLength: 16 },
        { backend: 'Line 2', maxLength: 16 }
      ];
    }
    return [];
  }

  function hasPhotoUpload(tags) {
    return hasAnyTag(tags, PHOTO_TAGS);
  }

  // Locket shape detection — matches PDP's #locket-type hidden input logic
  function getLocketType(tags) {
    var lower = tags.map(function (t) { return t.toLowerCase(); });
    if (lower.indexOf('locket-heart') > -1) return 'heart';
    if (lower.indexOf('locket-oval-horizontal') > -1) return 'oval';
    if (lower.indexOf('locket-oval-verticle') > -1) return 'oval-vertical';
    if (lower.indexOf('cocktail-ring') > -1) return 'cocktail-ring';
    if (lower.indexOf('locket-dog-tag') > -1) return 'dog-tag';
    if (lower.indexOf('locket-rectangle-vertical') > -1) return 'rectangle-vertical';
    if (lower.indexOf('locket-rectangle') > -1) return 'rectangle';
    if (lower.indexOf('locket-memorial-frame') > -1) return 'memorial-frame';
    return 'circle'; // default
  }

  // Build shape-clipped preview HTML — matches PDP SVG clip path approach exactly
  function getShapePreviewHtml(locketType, imgUrl) {
    var src = escapeHtml(imgUrl);
    var sf = 0.8, iw = 643 * sf, ih = 643 * sf;
    if (locketType === 'heart') {
      var hx = (500.18 - iw) / 2, hy = (472.42 - ih) / 2;
      return '<svg class="stack-sheet__photo-preview stack-sheet__photo-preview--svg" viewBox="0 0 500.18 472.42" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><clipPath id="stack-clip-heart"><path d="M250.22,61.56C271.92,25.59,328.01-3.61,386.03,.47c52.56,3.7,98.57,53.91,110.13,111.91,8.76,43.91,2.9,88.05-17.89,134.93-18.71,42.18-47.51,77.08-70.52,102.77-42.06,46.96-92.23,78.26-157.66,122.24-65.43-43.97-115.6-75.28-157.66-122.24-23.01-25.69-51.81-60.58-70.52-102.77C1.11,200.42-4.74,156.28,4.02,112.37,15.58,54.38,61.58,4.17,114.15,.47c58.02-4.08,114.11,25.12,135.81,61.09h.27Z"/></clipPath></defs><image width="' + iw + '" height="' + ih + '" x="' + hx + '" y="' + hy + '" clip-path="url(#stack-clip-heart)" xlink:href="' + src + '"></image></svg>';
    }
    if (locketType === 'oval') {
      var ox = (500.07 - iw) / 2, oy = (375.03 - ih) / 2;
      return '<svg class="stack-sheet__photo-preview stack-sheet__photo-preview--svg" viewBox="0 0 500.07 375.03" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><clipPath id="stack-clip-oval"><path d="M.43,187.78c-.64-12.17,1.2-24.05,4.2-35.78,1.73-6.77,3.97-13.37,6.65-19.83,3.44-8.3,7.58-16.21,12.32-23.82,4.86-7.8,10.33-15.11,16.3-22.09,5.49-6.42,11.4-12.41,17.65-18.08,8.3-7.54,17.12-14.44,26.52-20.55,6.38-4.14,12.92-8.05,19.54-11.78,4.69-2.64,9.63-4.86,14.49-7.2,3.37-1.63,6.74-3.28,10.2-4.68,5.38-2.18,10.81-4.26,16.29-6.18,5.39-1.89,10.8-3.8,16.33-5.24,9.35-2.44,18.74-4.75,28.2-6.66,7.53-1.52,15.18-2.61,22.82-3.47,8.03-.9,16.11-1.4,24.17-1.88,5.81-.34,11.65-.59,17.47-.46,7.53.17,15.06.63,22.57,1.22,6.47.5,12.92,1.26,19.36,2.08,4.51.57,9,1.33,13.46,2.21,6.28,1.23,12.55,2.52,18.76,4.01,8.62,2.07,17.17,4.43,25.49,7.5,5.6,2.06,11.21,4.12,16.75,6.32,8.42,3.34,16.53,7.35,24.56,11.58,11.8,6.23,23,13.36,33.58,21.47,4.53,3.47,8.83,7.23,13.17,10.93,6.39,5.45,12.22,11.48,17.75,17.79,6.16,7.03,11.77,14.48,16.76,22.39,4.59,7.27,8.75,14.78,11.97,22.75,2.32,5.74,4.42,11.58,6.24,17.5,2.3,7.47,3.92,15.12,4.75,22.91,.39,3.69,.65,7.4,.9,11.11,.17,2.53,.46,5.09,.27,7.6-.52,6.9-1.05,13.8-1.98,20.65-1.23,9.08-3.61,17.93-6.77,26.53-2.37,6.44-5.05,12.75-8.25,18.85-3.49,6.65-7.21,13.16-11.68,19.19-3.79,5.11-7.62,10.23-11.8,15.02-4.63,5.3-9.49,10.43-14.58,15.3-8.59,8.22-17.91,15.6-27.86,22.12-6.33,4.14-12.77,8.12-19.35,11.86-5.12,2.91-10.51,5.34-15.8,7.93-2.87,1.41-5.75,2.83-8.7,4.04-5.41,2.22-10.84,4.44-16.35,6.39-5.94,2.1-11.92,4.14-17.99,5.75-8.89,2.36-17.85,4.54-26.86,6.39-7.12,1.47-14.35,2.5-21.57,3.42-6.57.84-13.17,1.61-19.78,1.79-12.21.34-24.45.49-36.66.23-7.61-.16-15.21-1.03-22.78-1.89-6.78-.77-13.53-1.9-20.28-2.99-9.01-1.46-17.89-3.55-26.65-6.13-6.48-1.91-12.94-3.89-19.33-6.06-5.18-1.76-10.32-3.66-15.35-5.82-6.98-3-13.97-6.03-20.69-9.55-7.32-3.83-14.4-8.13-21.49-12.4-7.35-4.43-14.2-9.59-20.87-14.98-8.58-6.93-16.59-14.48-23.96-22.68-6.61-7.36-12.66-15.15-17.94-23.52-2.66-4.22-5.07-8.6-7.43-12.99-1.78-3.3-3.42-6.68-4.92-10.12-5.52-12.65-9.31-25.81-10.99-39.51-.75-6.12-.88-12.31-1.29-18.48.14,0,.28,0,.43-.01Z"/></clipPath></defs><image width="' + iw + '" height="' + ih + '" x="' + ox + '" y="' + oy + '" clip-path="url(#stack-clip-oval)" xlink:href="' + src + '"></image></svg>';
    }
    if (locketType === 'oval-vertical') {
      var vx = (375.03 - iw) / 2, vy = (500.07 - ih) / 2;
      return '<svg class="stack-sheet__photo-preview stack-sheet__photo-preview--svg" viewBox="0 0 375.03 500.07" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><clipPath id="stack-clip-ovalv"><path d="M187.78,499.64c-12.17.64-24.05-1.2-35.78-4.2-6.77-1.73-13.37-3.97-19.83-6.65-8.3-3.44-16.21-7.58-23.82-12.32-7.8-4.86-15.11-10.33-22.09-16.3-6.42-5.49-12.41-11.4-18.08-17.65-7.54-8.3-14.44-17.12-20.55-26.52-4.14-6.38-8.05-12.92-11.78-19.54-2.64-4.69-4.86-9.63-7.2-14.49-1.63-3.37-3.28-6.74-4.68-10.2-2.18-5.38-4.26-10.81-6.18-16.29-1.89-5.39-3.8-10.8-5.24-16.33-2.44-9.35-4.75-18.74-6.66-28.2-1.52-7.53-2.61-15.18-3.47-22.82-.9-8.03-1.4-16.11-1.88-24.17-.34-5.81-.59-11.65-.46-17.47.17-7.53.63-15.06,1.22-22.57.5-6.47,1.26-12.92,2.08-19.36.57-4.51,1.33-9,2.21-13.46,1.23-6.28,2.52-12.55,4.01-18.76,2.07-8.62,4.43-17.17,7.5-25.49,2.06-5.6,4.12-11.21,6.32-16.75,3.34-8.42,7.35-16.53,11.58-24.56,6.23-11.8,13.36-23,21.47-33.58,3.47-4.53,7.23-8.83,10.93-13.17,5.45-6.39,11.48-12.22,17.79-17.75,7.03-6.16,14.48-11.77,22.39-16.76,7.27-4.59,14.78-8.75,22.75-11.97,5.74-2.32,11.58-4.42,17.5-6.24,7.47-2.3,15.12-3.92,22.91-4.75,3.69-.39,7.4-.65,11.11-.9,2.53-.17,5.09-.46,7.6-.27,6.9.52,13.8,1.05,20.65,1.98,9.08,1.23,17.93,3.61,26.53,6.77,6.44,2.37,12.75,5.05,18.85,8.25,6.65,3.49,13.16,7.21,19.19,11.68,5.11,3.79,10.23,7.62,15.02,11.8,5.3,4.63,10.43,9.49,15.3,14.58,8.22,8.59,15.6,17.91,22.12,27.86,4.14,6.33,8.12,12.77,11.86,19.35,2.91,5.12,5.34,10.51,7.93,15.8,1.41,2.87,2.83,5.75,4.04,8.7,2.22,5.41,4.44,10.84,6.39,16.35,2.1,5.94,4.14,11.92,5.75,17.99,2.36,8.89,4.54,17.85,6.39,26.86,1.47,7.12,2.5,14.35,3.42,21.57.84,6.57,1.61,13.17,1.79,19.78.34,12.21.49,24.45.23,36.66-.16,7.61-1.03,15.21-1.89,22.78-.77,6.78-1.9,13.53-2.99,20.28-1.46,9.01-3.55,17.89-6.13,26.65-1.91,6.48-3.89,12.94-6.06,19.33-1.76,5.18-3.66,10.32-5.82,15.35-3,6.98-6.03,13.97-9.55,20.69-3.83,7.32-8.13,14.4-12.4,21.49-4.43,7.35-9.59,14.2-14.98,20.87-6.93,8.58-14.48,16.59-22.68,23.96-7.36,6.61-15.15,12.66-23.52,17.94-4.22,2.66-8.6,5.07-12.99,7.43-3.3,1.78-6.68,3.42-10.12,4.92-12.65,5.52-25.81,9.31-39.51,10.99-6.12.75-12.31.88-18.48,1.29,0-.14,0-.28-.01-.43Z"/></clipPath></defs><image width="' + iw + '" height="' + ih + '" x="' + vx + '" y="' + vy + '" clip-path="url(#stack-clip-ovalv)" xlink:href="' + src + '"></image></svg>';
    }
    if (locketType === 'cocktail-ring') {
      var cx = (353.03 - iw) / 2, cy = (500.07 - ih) / 2;
      return '<svg class="stack-sheet__photo-preview stack-sheet__photo-preview--svg" viewBox="0 0 353.03 500.07" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><clipPath id="stack-clip-cocktail"><polygon points="64.86,500.07 0,435.21 0,64.86 64.86,0 288.17,0 353.03,64.86 353.03,435.21 288.17,500.07"/></clipPath></defs><image width="' + iw + '" height="' + ih + '" x="' + cx + '" y="' + cy + '" clip-path="url(#stack-clip-cocktail)" xlink:href="' + src + '"></image></svg>';
    }
    if (locketType === 'dog-tag') {
      var dx = (287.548 - iw) / 2, dy = (500.07 - ih) / 2;
      return '<svg class="stack-sheet__photo-preview stack-sheet__photo-preview--svg" viewBox="0 0 287.548 500.07" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><clipPath id="stack-clip-dogtag"><path d="M143.78,500.07c-86.475,0,-143.78,-40.125,-143.78,-56.384V56.384C0,40.125,57.306,0,143.78,0s143.774,40.125,143.774,56.384v387.301c0,16.259,-57.299,56.384,-143.774,56.384Z"/></clipPath></defs><image width="' + iw + '" height="' + ih + '" x="' + dx + '" y="' + dy + '" clip-path="url(#stack-clip-dogtag)" xlink:href="' + src + '"></image></svg>';
    }
    if (locketType === 'rectangle') {
      return '<img class="stack-sheet__photo-preview stack-sheet__photo-preview--rectangle" src="' + src + '" alt="Uploaded photo">';
    }
    if (locketType === 'rectangle-vertical') {
      return '<img class="stack-sheet__photo-preview stack-sheet__photo-preview--rectangle-vertical" src="' + src + '" alt="Uploaded photo">';
    }
    if (locketType === 'memorial-frame') {   // square window, slightly rounded corners
      return '<img class="stack-sheet__photo-preview" style="border-radius:4px" src="' + src + '" alt="Uploaded photo">';
    }
    // circle default
    return '<img class="stack-sheet__photo-preview" src="' + src + '" alt="Uploaded photo">';
  }

  /* ===== RENDER SHEET CONTENT ===== */

  function renderSheetContent(product) {
    var content = document.getElementById('stack-sheet-content');
    if (!content) return;

    var variant = getSelectedVariant(product);
    var price = variant ? variant.price : product.price;
    var image = (variant && variant.image) ? variant.image : product.featuredImage;

    var html = '';

    // Header
    html += '<div class="stack-sheet__header">';
    html += '<img class="stack-sheet__product-img" id="sheet-product-img" src="' + escapeHtml(image || '') + '" alt="' + escapeHtml(product.title) + '" width="80" height="80">';
    html += '<div class="stack-sheet__product-info">';
    html += '<div class="stack-sheet__product-title">' + escapeHtml(product.title) + '</div>';
    html += '<div class="stack-sheet__product-price" id="sheet-product-price">' + formatMoney(price) + '</div>';
    html += '</div>';
    html += '</div>';

    // Variant options
    for (var i = 0; i < product.options.length; i++) {
      var opt = product.options[i];
      if (opt.values.length <= 1 && product.variants.length <= 1) continue; // skip if only "Default Title"

      html += '<div class="stack-sheet__option-group">';
      html += '<div class="stack-sheet__option-label">' + escapeHtml(opt.name) + ': <span id="sheet-opt-val-' + i + '">' + escapeHtml(sheetSelectedOptions[opt.name] || '') + '</span></div>';

      if (isColorOption(opt.name)) {
        // Color swatches — use PDP-matching swatch images/colors
        html += '<div class="stack-sheet__swatches">';
        for (var j = 0; j < opt.values.length; j++) {
          var val = opt.values[j];
          var active = sheetSelectedOptions[opt.name] === val ? ' stack-sheet__swatch--active' : '';
          var sw = getSwatchStyle(product, opt.name, val);
          var swStyle = '';
          if (sw.image) swStyle += 'background-image:url(\'' + sw.image + '\');';
          if (sw.fallback) swStyle += 'background-color:' + sw.fallback + ';';
          html += '<button type="button" class="stack-sheet__swatch' + active + '" data-option="' + escapeHtml(opt.name) + '" data-value="' + escapeHtml(val) + '" style="' + swStyle + '" title="' + escapeHtml(val) + '"></button>';
        }
        html += '</div>';
      } else {
        // Pill buttons
        html += '<div class="stack-sheet__pills">';
        for (var k = 0; k < opt.values.length; k++) {
          var pVal = opt.values[k];
          var pActive = sheetSelectedOptions[opt.name] === pVal ? ' stack-sheet__pill--active' : '';
          html += '<button type="button" class="stack-sheet__pill' + pActive + '" data-option="' + escapeHtml(opt.name) + '" data-value="' + escapeHtml(pVal) + '">' + escapeHtml(pVal) + '</button>';
        }
        html += '</div>';
      }

      html += '</div>';
    }

    // Engraving fields
    var engraveType = getEngravingType(product.tags);
    var engraveLines = getEngraveLines(product.tags);
    var showFontPicker = engraveType !== null; // font picker for standard/ornament

    if (showFontPicker || engraveLines.length > 0) {
      html += '<div class="stack-sheet__engrave-group">';
      html += '<div class="stack-sheet__engrave-label">Personalization</div>';

      // Font picker — filtered per product type
      if (showFontPicker) {
        var productFonts = getFontsForProduct(product.tags);
        var activeCode = getDefaultFontCode(product.tags);

        html += '<div class="stack-sheet__font-picker">';
        for (var f = 0; f < productFonts.length; f++) {
          var font = productFonts[f];
          var fActive = font.code === activeCode ? ' stack-sheet__font-btn--active' : '';
          html += '<button type="button" class="stack-sheet__font-btn' + fActive + '" data-font="' + escapeHtml(font.code) + '" data-css="' + escapeHtml(font.css) + '" style="font-family:' + font.css + ';" title="' + escapeHtml(font.label) + '">Aa</button>';
        }
        html += '</div>';
        sheetProperties['_FONT'] = activeCode;
      }

      // Text inputs — use type-specific line config
      var activeFontCss = showFontPicker ? getFontCssByCode(getDefaultFontCode(product.tags)) : '';
      for (var l = 0; l < engraveLines.length; l++) {
        var line = engraveLines[l];
        var lineNum = l + 1;
        var fontStyle = activeFontCss ? ' style="font-family:' + activeFontCss + ';"' : '';
        html += '<input type="text" class="stack-sheet__text-input" data-prop="' + escapeHtml(line.backend) + '" placeholder="Line ' + lineNum + '" maxlength="' + line.maxLength + '" spellcheck="false" autocorrect="off"' + fontStyle + '>';
        html += '<div class="stack-sheet__char-count" data-for="' + escapeHtml(line.backend) + '">' + line.maxLength + ' characters max</div>';
      }

      html += '</div>';
    }

    // Photo upload
    if (hasPhotoUpload(product.tags)) {
      html += '<div class="stack-sheet__photo-area">';
      html += '<div class="stack-sheet__photo-label">Upload Photo</div>';
      html += '<div class="stack-sheet__photo-upload" id="sheet-photo-upload">';
      html += '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>';
      html += '<span>Tap to upload</span>';
      html += '</div>';
      html += '<input type="file" id="sheet-photo-input" accept="image/*" style="display:none;">';
      html += '</div>';
    }

    // Add to Stack button
    var btnDisabled = (product.variants.length > 1 && (!variant || !variant.available)) ? ' disabled' : '';
    html += '<button type="button" class="stack-sheet__add-btn" id="sheet-add-btn"' + btnDisabled + '>Add to Stack</button>';

    content.innerHTML = html;

    // Attach sheet event handlers
    attachSheetHandlers(product);
  }

  /* ===== SHEET EVENT HANDLERS ===== */

  function attachSheetHandlers(product) {
    var content = document.getElementById('stack-sheet-content');
    if (!content) return;

    // Swatch and pill clicks
    var optBtns = content.querySelectorAll('.stack-sheet__swatch, .stack-sheet__pill');
    for (var i = 0; i < optBtns.length; i++) {
      optBtns[i].addEventListener('click', function () {
        var optName = this.getAttribute('data-option');
        var optVal = this.getAttribute('data-value');
        sheetSelectedOptions[optName] = optVal;
        updateSheetAfterOptionChange(product);
      });
    }

    // Font buttons
    var fontBtns = content.querySelectorAll('.stack-sheet__font-btn');
    for (var f = 0; f < fontBtns.length; f++) {
      fontBtns[f].addEventListener('click', function () {
        var fontVal = this.getAttribute('data-font');
        var fontCss = this.getAttribute('data-css');
        sheetProperties['_FONT'] = fontVal;
        // Update active state
        var all = content.querySelectorAll('.stack-sheet__font-btn');
        for (var x = 0; x < all.length; x++) all[x].classList.remove('stack-sheet__font-btn--active');
        this.classList.add('stack-sheet__font-btn--active');
        // Update text inputs to show selected font
        var inputs = content.querySelectorAll('.stack-sheet__text-input');
        for (var y = 0; y < inputs.length; y++) inputs[y].style.fontFamily = fontCss;
      });
    }

    // Text inputs — update properties on input
    var textInputs = content.querySelectorAll('.stack-sheet__text-input');
    for (var t = 0; t < textInputs.length; t++) {
      textInputs[t].addEventListener('input', function () {
        var propKey = this.getAttribute('data-prop');
        sheetProperties[propKey] = this.value;
        var maxLen = parseInt(this.getAttribute('maxlength'));
        var remaining = maxLen - this.value.length;
        var countEl = this.parentNode.querySelector('.stack-sheet__char-count[data-for="' + propKey + '"]');
        if (countEl) countEl.textContent = remaining + ' of ' + maxLen + ' characters remaining';
      });
    }

    // Photo upload
    var photoUpload = document.getElementById('sheet-photo-upload');
    var photoInput = document.getElementById('sheet-photo-input');
    if (photoUpload && photoInput) {
      photoUpload.addEventListener('click', function () {
        photoInput.click();
      });
      photoInput.addEventListener('change', function () {
        if (this.files && this.files[0]) {
          handlePhotoFile(this.files[0], product);
        }
      });
    }

    // Add to Stack button
    var addBtn = document.getElementById('sheet-add-btn');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        addFromSheet(product);
      });
    }
  }

  function updateSheetAfterOptionChange(product) {
    var variant = getSelectedVariant(product);
    var content = document.getElementById('stack-sheet-content');
    if (!content) return;

    // Update price
    var priceEl = document.getElementById('sheet-product-price');
    if (priceEl && variant) {
      priceEl.textContent = formatMoney(variant.price);
    }

    // Update image if variant has one
    var imgEl = document.getElementById('sheet-product-img');
    if (imgEl && variant && variant.image) {
      imgEl.src = variant.image;
    }

    // Update swatch/pill active states
    var swatches = content.querySelectorAll('.stack-sheet__swatch');
    for (var i = 0; i < swatches.length; i++) {
      var optName = swatches[i].getAttribute('data-option');
      var optVal = swatches[i].getAttribute('data-value');
      if (sheetSelectedOptions[optName] === optVal) {
        swatches[i].classList.add('stack-sheet__swatch--active');
      } else {
        swatches[i].classList.remove('stack-sheet__swatch--active');
      }
    }

    var pills = content.querySelectorAll('.stack-sheet__pill');
    for (var j = 0; j < pills.length; j++) {
      var pOptName = pills[j].getAttribute('data-option');
      var pOptVal = pills[j].getAttribute('data-value');
      if (sheetSelectedOptions[pOptName] === pOptVal) {
        pills[j].classList.add('stack-sheet__pill--active');
      } else {
        pills[j].classList.remove('stack-sheet__pill--active');
      }
    }

    // Update option value display
    for (var k = 0; k < product.options.length; k++) {
      var valSpan = document.getElementById('sheet-opt-val-' + k);
      if (valSpan) {
        valSpan.textContent = sheetSelectedOptions[product.options[k].name] || '';
      }
    }

    // Update Add button disabled state
    var addBtn = document.getElementById('sheet-add-btn');
    if (addBtn) {
      if (product.variants.length > 1 && (!variant || !variant.available)) {
        addBtn.disabled = true;
      } else {
        addBtn.disabled = false;
      }
    }
  }

  /* ===== PHOTO UPLOAD (Pintura + Cloudinary) ===== */

  var activePinturaEditor = null;

  function handlePhotoFile(file, product) {
    // Check if Pintura is available (global `pintura` with appendDefaultEditor)
    if (typeof pintura !== 'undefined' && pintura.appendDefaultEditor) {
      openPinturaEditor(file, product);
    } else {
      // Fallback: upload directly without editing
      uploadToCloudinary(file, product);
    }
  }

  function openPinturaEditor(file, product) {
    var overlayEl = document.getElementById('stack-pintura-overlay');
    var containerEl = document.getElementById('stack-pintura-container');
    if (!overlayEl || !containerEl) {
      uploadToCloudinary(file, product);
      return;
    }

    var locketType = getLocketType(product.tags);

    overlayEl.style.display = 'block';
    containerEl.innerHTML =
      '<div id="stack-pintura-editor" style="height:calc(100% - 60px);"></div>' +
      '<div style="display:flex;gap:12px;justify-content:center;padding:10px 16px;background:#f5f5f5;">' +
        '<button type="button" id="stack-pintura-cancel" style="flex:1;max-width:180px;height:40px;border:2px solid #ccc;border-radius:20px;background:#fff;font-size:14px;font-weight:600;cursor:pointer;">Cancel</button>' +
        '<button type="button" id="stack-pintura-confirm" style="flex:1;max-width:180px;height:40px;border:none;border-radius:20px;background:#d4af6e;color:#000000;font-size:14px;font-weight:600;cursor:pointer;">Use Photo</button>' +
      '</div>';

    // Post-processing: only circle gets canvas mask; other shapes pass through
    var postProcess = function (imageData) { return imageData; };
    if (locketType === 'circle') {
      postProcess = function (imageData) {
        return new Promise(function (resolve) {
          var canvas = document.createElement('canvas');
          canvas.width = imageData.width;
          canvas.height = imageData.height;
          var ctx = canvas.getContext('2d');
          ctx.putImageData(imageData, 0, 0);
          ctx.globalCompositeOperation = 'destination-in';
          ctx.fillStyle = 'black';
          ctx.beginPath();
          ctx.arc(imageData.width * 0.5, imageData.height * 0.5, imageData.width * 0.5, 0, 2 * Math.PI);
          ctx.fill();
          resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
        });
      };
    }

    activePinturaEditor = pintura.appendDefaultEditor('#stack-pintura-editor', {
      src: file,
      imageCropAspectRatio: locketType === 'rectangle' ? 1.332 : locketType === 'rectangle-vertical' ? 1 / 1.332 : 1,
      cropEnableButtonFlipHorizontal: false,
      cropEnableButtonFlipVertical: false,
      imageCropLimitToImage: true,
      willRenderCanvas: function (shapes, state) {
        var utilVisibility = state.utilVisibility;
        var selectionRect = state.selectionRect;
        var lineColor = state.lineColor;
        var backgroundColor = state.backgroundColor;
        if (utilVisibility.crop <= 0) return shapes;
        var x = selectionRect.x, y = selectionRect.y;
        var w = selectionRect.width, h = selectionRect.height;

        var equippedShape;
        switch (locketType) {
          case 'heart':
            equippedShape = {
              x: selectionRect.x, y: selectionRect.y,
              width: selectionRect.width, height: selectionRect.height,
              opacity: 0.5, backgroundSize: 'contain',
              backgroundImage: 'https://cdn.shopify.com/s/files/1/0586/0117/7256/files/heart_100.png'
            };
            break;
          case 'oval':
            equippedShape = {
              x: selectionRect.x, y: selectionRect.y,
              width: selectionRect.width, height: selectionRect.height,
              opacity: 0.5, backgroundSize: 'contain',
              backgroundImage: 'https://cdn.shopify.com/s/files/1/0586/0117/7256/files/oval_horizontal.png'
            };
            break;
          case 'oval-vertical':
            equippedShape = {
              x: selectionRect.x, y: selectionRect.y,
              width: selectionRect.width, height: selectionRect.height,
              opacity: 0.5, backgroundSize: 'contain',
              backgroundImage: 'https://cdn.shopify.com/s/files/1/0586/0117/7256/files/oval_verticle.png'
            };
            break;
          case 'rectangle':
          case 'rectangle-vertical':
            equippedShape = {
              x: x + w * 0.5, y: y + h * 0.5,
              opacity: utilVisibility.crop, inverted: true,
              backgroundColor: [backgroundColor[0], backgroundColor[1], backgroundColor[2], 0.5],
              strokeWidth: 1,
              strokeColor: [lineColor[0], lineColor[1], lineColor[2]]
            };
            break;
          default: // circle
            equippedShape = {
              x: x + w * 0.5, y: y + h * 0.5,
              rx: w * 0.5, ry: h * 0.5,
              opacity: utilVisibility.crop, inverted: true,
              backgroundColor: [backgroundColor[0], backgroundColor[1], backgroundColor[2], 0.5],
              strokeWidth: 1,
              strokeColor: [lineColor[0], lineColor[1], lineColor[2]]
            };
            break;
        }

        var merged = Object.assign({}, shapes);
        merged.interfaceShapes = [equippedShape].concat(shapes.interfaceShapes);
        return merged;
      },
      imageReader: pintura.createDefaultImageReader(),
      imageWriter: pintura.createDefaultImageWriter({
        mimeType: 'image/jpeg',
        quality: 0.5,
        targetSize: { width: 1000, height: 1000, fit: 'contain', upscale: false },
        postprocessImageData: postProcess
      })
    });

    // Add loading spinner (matches PDP)
    var pinturaRoot = document.querySelector('#stack-pintura-editor .PinturaRoot');
    if (pinturaRoot) {
      var spinner = document.createElement('div');
      spinner.className = 'editorLoading';
      spinner.innerHTML = '<div class="lds-dual-ring"></div> <span style="text-align:center;">Loading...</span>';
      pinturaRoot.appendChild(spinner);
    }

    document.getElementById('stack-pintura-cancel').addEventListener('click', function () {
      closePinturaOverlay();
    });

    document.getElementById('stack-pintura-confirm').addEventListener('click', function () {
      if (!activePinturaEditor) return;
      var confirmBtn = this;
      confirmBtn.textContent = 'Processing...';
      confirmBtn.disabled = true;

      activePinturaEditor.processImage().then(function (result) {
        closePinturaOverlay();
        uploadToCloudinary(result.dest, product);
      }).catch(function () {
        closePinturaOverlay();
        showToast('Photo processing failed. Please try again.');
      });
    });
  }

  function closePinturaOverlay() {
    var overlayEl = document.getElementById('stack-pintura-overlay');
    var containerEl = document.getElementById('stack-pintura-container');
    if (overlayEl) overlayEl.style.display = 'none';
    if (containerEl) containerEl.innerHTML = '';
    activePinturaEditor = null;
  }

  function uploadToCloudinary(file, product) {
    var uploadArea = document.getElementById('sheet-photo-upload');
    if (uploadArea) {
      uploadArea.innerHTML = '<span>Uploading...</span>';
    }

    var formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'ml_unsigned');

    fetch('https://api.cloudinary.com/v1_1/dgwldii6i/image/upload', {
      method: 'POST',
      body: formData
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Upload failed');
        return res.json();
      })
      .then(function (data) {
        sheetPhotoUrl = data.secure_url || data.url || '';
        sheetProperties['photo'] = sheetPhotoUrl;
        // Map locket type to _circlerender value (matches PDP)
        var lt = getLocketType(product.tags);
        var renderMap = { heart: 'HEART', oval: 'OVAL', 'oval-vertical': 'OVAL_VERTICAL', rectangle: 'SQUARE', 'rectangle-vertical': 'SQUARE', 'memorial-frame': 'SQUARE' };
        sheetProperties['_circlerender'] = renderMap[lt] || 'CIRCLE';

        // Build preview URL matching PDP approach: extract splitImage, apply Cloudinary transforms
        var rawUrl = data.url || sheetPhotoUrl;
        var previewSrc = sheetPhotoUrl;
        var httpPrefix = 'http://res.cloudinary.com/dgwldii6i/image/upload/';
        var httpsPrefix = 'https://res.cloudinary.com/dgwldii6i/image/upload/';
        var splitImage = '';
        if (rawUrl.indexOf(httpPrefix) > -1) {
          splitImage = rawUrl.split(httpPrefix)[1];
        } else if (rawUrl.indexOf(httpsPrefix) > -1) {
          splitImage = rawUrl.split(httpsPrefix)[1];
        }
        if (splitImage) {
          // PDP uses c_scale,h_250,w_250 for circle and heart, raw URL for ovals/rectangle
          if (lt === 'circle' || lt === 'heart') {
            previewSrc = 'https://res.cloudinary.com/dgwldii6i/image/upload/c_scale,h_250,w_250/' + splitImage;
          } else {
            previewSrc = 'https://res.cloudinary.com/dgwldii6i/image/upload/' + splitImage;
          }
        }

        // Show preview clipped to locket shape
        if (uploadArea) {
          var previewHtml = getShapePreviewHtml(lt, previewSrc);
          uploadArea.className = uploadArea.className.replace(' stack-sheet__photo-upload--has-preview', '') + ' stack-sheet__photo-upload--has-preview';
          uploadArea.innerHTML = previewHtml + '<span class="stack-sheet__photo-change">Tap to change</span>';
        }
      })
      .catch(function (err) {
        console.error('Cloudinary upload error:', err);
        if (uploadArea) {
          uploadArea.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>' +
            '<span>Upload failed. Tap to retry.</span>';
        }
        showToast('Photo upload failed. Please try again.');
      });
  }

  /* ===== ADD FROM SHEET ===== */

  function addFromSheet(product) {
    var variant = getSelectedVariant(product);
    if (product.variants.length > 1 && (!variant || !variant.available)) {
      showToast('Please select an available option.');
      return;
    }

    // Use first variant if only one exists
    if (!variant && product.variants.length === 1) {
      variant = product.variants[0];
    }
    if (!variant) {
      showToast('Please select options.');
      return;
    }

    // Require engraving text if product has engraving fields
    var engraveLines = getEngraveLines(product.tags);
    if (engraveLines.length > 0) {
      var hasText = false;
      for (var el = 0; el < engraveLines.length; el++) {
        var val = sheetProperties[engraveLines[el].backend];
        if (val && val.trim()) { hasText = true; break; }
      }
      if (!hasText) {
        showToast('Please enter your personalization text.');
        return;
      }
    }

    // Require photo if product has photo upload
    if (hasPhotoUpload(product.tags) && !sheetPhotoUrl) {
      showToast('Please upload a photo.');
      return;
    }

    // Collect all properties — mirror hidden/visible keys to match PDP format
    var props = {};
    var hasEngraving = false;
    for (var key in sheetProperties) {
      if (sheetProperties.hasOwnProperty(key) && sheetProperties[key]) {
        props[key] = sheetProperties[key];
        // Mirror _TEXT* → visible "Line *" for checkout display
        if (key.indexOf('_TEXT') === 0) {
          var lineNum = key.replace('_TEXT', '');
          props['Line ' + lineNum] = sheetProperties[key];
          hasEngraving = true;
        }
        // Mirror "Line *" → hidden "_TEXT*" for admin/fulfillment
        if (key.indexOf('Line ') === 0) {
          var textNum = key.replace('Line ', '');
          props['_TEXT' + textNum] = sheetProperties[key];
          hasEngraving = true;
        }
      }
    }
    // Flag engraving for fulfillment (matches PDP)
    if (hasEngraving) {
      props['_ENGRAVING'] = 'true';
    }

    var image = (variant.image) ? variant.image : product.featuredImage;
    var summary = buildOptionsSummary(product);

    addItem({
      variantId: String(variant.id),
      productId: String(product.id),
      title: product.title,
      price: variant.price,
      image: image,
      handle: product.handle,
      properties: props,
      optionsSummary: summary
    });

    // Mark the card as selected
    if (sheetCard) setCardSelected(sheetCard);

    closeSheet();
    showToast('Added to your stack!');
  }

  /* ===== RENDER ===== */

  function renderItemRow(item) {
    var optLine = item.optionsSummary ? '<span class="stack-item__options">' + escapeHtml(item.optionsSummary) + '</span>' : '';
    return (
      '<div class="stack-item" data-variant-id="' + item.variantId + '" data-product-id="' + item.productId + '">' +
        '<img class="stack-item__thumb" src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" width="50" height="50" loading="lazy">' +
        '<div class="stack-item__details">' +
          '<span class="stack-item__title">' + escapeHtml(item.title) + '</span>' +
          optLine +
          '<span class="stack-item__price">' + formatMoney(item.price) + '</span>' +
        '</div>' +
        '<button class="stack-item__remove" aria-label="Remove ' + escapeHtml(item.title) + '" data-product-id="' + item.productId + '">&times;</button>' +
      '</div>'
    );
  }

  function renderMobileDrawer() {
    var count = getItemCount();
    var pct = getDiscountPercent();

    var labelEl = document.querySelector('[data-drawer-label]');
    var savingsEl = document.querySelector('[data-drawer-savings]');

    if (labelEl) {
      labelEl.textContent = 'Your Stack: ' + count + ' item' + (count !== 1 ? 's' : '') + (count > 0 ? ' \u00B7 ' + formatMoney(getDiscountedTotal()) : '');
    }
    if (savingsEl) {
      savingsEl.textContent = pct > 0 ? 'Save ' + Math.round(pct * 100) + '%' : '';
    }

    var itemList = document.getElementById('stack-drawer-items');
    if (itemList) {
      if (count === 0) {
        itemList.innerHTML = '<div style="text-align:center;padding:24px 16px;color:#999;font-size:14px;">No items in your stack yet</div>';
      } else {
        var html = '';
        for (var i = 0; i < stackItems.length; i++) {
          html += renderItemRow(stackItems[i]);
        }
        itemList.innerHTML = html;
        attachRemoveHandlers(itemList);
      }
    }

    var drawerSubtotal = document.querySelector('[data-drawer-subtotal]');
    var drawerDiscount = document.querySelector('[data-drawer-discount]');
    var drawerTotal = document.querySelector('[data-drawer-total]');

    if (drawerSubtotal) drawerSubtotal.textContent = formatMoney(getSubtotal());
    if (drawerDiscount) drawerDiscount.textContent = '-' + formatMoney(getDiscountAmount());
    if (drawerTotal) drawerTotal.textContent = formatMoney(getDiscountedTotal());

    var drawerCheckout = document.getElementById('stack-drawer-checkout');
    if (drawerCheckout) drawerCheckout.disabled = count === 0;
  }

  function renderDesktopSummary() {
    var container = document.getElementById('stack-summary-desktop');
    if (!container) return;

    var count = getItemCount();

    var itemList = document.getElementById('stack-summary-items');
    if (itemList) {
      if (count === 0) {
        itemList.innerHTML = '<div style="text-align:center;padding:20px 0;color:#999;font-size:14px;">' + getNextTierMessage() + '</div>';
      } else {
        var html = '';
        for (var i = 0; i < stackItems.length; i++) {
          html += renderItemRow(stackItems[i]);
        }
        itemList.innerHTML = html;
        attachRemoveHandlers(itemList);
      }
    }

    var summarySubtotal = document.querySelector('[data-summary-subtotal]');
    var summaryDiscount = document.querySelector('[data-summary-discount]');
    var summaryTotal = document.querySelector('[data-summary-total]');

    if (summarySubtotal) summarySubtotal.textContent = formatMoney(getSubtotal());
    if (summaryDiscount) summaryDiscount.textContent = '-' + formatMoney(getDiscountAmount());
    if (summaryTotal) summaryTotal.textContent = formatMoney(getDiscountedTotal());

    var upsellEl = document.querySelector('[data-summary-upsell]');
    if (upsellEl) {
      upsellEl.textContent = getNextTierMessage();
      upsellEl.style.display = count > 0 ? 'block' : 'none';
    }

    var checkoutBtn = document.getElementById('stack-summary-checkout');
    if (checkoutBtn) checkoutBtn.disabled = count === 0;
  }

  function attachRemoveHandlers(container) {
    var removeBtns = container.querySelectorAll('.stack-item__remove');
    for (var i = 0; i < removeBtns.length; i++) {
      removeBtns[i].addEventListener('click', function (e) {
        e.stopPropagation();
        var pid = this.getAttribute('data-product-id');
        removeItemByProduct(pid);
        syncAllCards();
      });
    }
  }

  /* ===== MOBILE DRAWER SHOW/HIDE ===== */
  var drawerExpanded = false;

  function getDrawer() {
    return document.getElementById('stack-drawer-mobile');
  }

  function getOverlay() {
    return document.querySelector('.stack-drawer__overlay');
  }

  function showDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.add('stack-drawer--visible');
  }

  function hideDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    collapseDrawer();
    drawer.classList.remove('stack-drawer--visible');
  }

  function expandDrawer() {
    if (drawerExpanded) return;
    drawerExpanded = true;
    var drawer = getDrawer();
    var overlay = getOverlay();
    if (drawer) drawer.classList.add('stack-drawer--expanded');
    if (overlay) overlay.classList.add('stack-drawer__overlay--visible');
    document.body.style.overflow = 'hidden';
  }

  function collapseDrawer() {
    if (!drawerExpanded) return;
    drawerExpanded = false;
    var drawer = getDrawer();
    var overlay = getOverlay();
    if (drawer) drawer.classList.remove('stack-drawer--expanded');
    if (overlay) overlay.classList.remove('stack-drawer__overlay--visible');
    document.body.style.overflow = '';
  }

  function toggleDrawer() {
    if (drawerExpanded) {
      collapseDrawer();
    } else {
      expandDrawer();
    }
  }

  /* ===== CENTRAL STATE CHANGE HANDLER ===== */
  function onStateChange() {
    var count = getItemCount();

    if (count > 0) {
      showDrawer();
    } else {
      hideDrawer();
    }

    renderMobileDrawer();
    renderDesktopSummary();
    updateFinalCTA();
  }

  /* ===== FINAL CTA ===== */
  function updateFinalCTA() {
    var btn = document.getElementById('stack-final-cta-btn');
    if (!btn) return;

    var count = getItemCount();
    if (count > 0) {
      btn.textContent = 'Checkout (' + count + ' item' + (count !== 1 ? 's' : '') + ') \u2014 ' + formatMoney(getDiscountedTotal());
    } else {
      btn.textContent = 'Build My Stack';
    }
  }

  function handleFinalCTA() {
    if (getItemCount() > 0) {
      handleCheckout();
    } else {
      var target = document.getElementById('stack-builder');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  /* ===== CHECKOUT ===== */
  function handleCheckout() {
    if (getItemCount() === 0) return;

    var btns = document.querySelectorAll('#stack-drawer-checkout, #stack-summary-checkout, #stack-final-cta-btn');
    for (var i = 0; i < btns.length; i++) {
      btns[i].disabled = true;
      btns[i].classList.add('stack-checkout-btn--loading');
    }

    // Build line items with properties
    var items = [];
    for (var j = 0; j < stackItems.length; j++) {
      var lineItem = {
        id: parseInt(stackItems[j].variantId, 10),
        quantity: 1
      };
      // Add properties if any exist
      if (stackItems[j].properties && Object.keys(stackItems[j].properties).length > 0) {
        lineItem.properties = stackItems[j].properties;
      }
      items.push(lineItem);
    }

    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to clear cart');
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to add items to cart');
        return res.json();
      })
      .then(function () {
        var pct = getDiscountPercent();
        var discountCode = '';
        if (pct === 0.10) discountCode = 'STACK10';
        else if (pct === 0.15) discountCode = 'STACK15';
        else if (pct === 0.20) discountCode = 'STACK20';
        else if (pct === 0.25) discountCode = 'STACK25';

        var url = '/checkout';
        if (discountCode) {
          url += '?discount=' + discountCode;
        }
        window.location.href = url;
      })
      .catch(function (err) {
        console.error('Stack checkout error:', err);
        showToast('Something went wrong. Please try again.');

        for (var k = 0; k < btns.length; k++) {
          btns[k].disabled = false;
          btns[k].classList.remove('stack-checkout-btn--loading');
        }
      });
  }

  /* ===== TOAST NOTIFICATION ===== */
  function showToast(message) {
    var existing = document.querySelector('.stack-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'stack-toast';
    toast.textContent = message;
    toast.setAttribute('role', 'alert');
    document.body.appendChild(toast);

    void toast.offsetHeight;
    toast.classList.add('stack-toast--visible');

    setTimeout(function () {
      toast.classList.remove('stack-toast--visible');
      setTimeout(function () {
        toast.remove();
      }, 300);
    }, 3000);
  }

  /* ===== SMOOTH SCROLL ===== */
  function initSmoothScroll() {
    var links = document.querySelectorAll('a[href^="#"]');
    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', function (e) {
        var href = this.getAttribute('href');
        if (href.length <= 1) return;
        var target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }

  /* ===== TOUCH SWIPE TO CLOSE DRAWER ===== */
  function initDrawerSwipe() {
    var bar = document.querySelector('.stack-drawer__bar');
    var drawer = getDrawer();
    if (!drawer || !bar) return;

    var startY = 0;
    var currentY = 0;
    var isDragging = false;

    bar.addEventListener('touchstart', function (e) {
      if (!drawerExpanded) return;
      startY = e.touches[0].clientY;
      currentY = startY;
      isDragging = true;
      drawer.style.transition = 'none';
    }, { passive: true });

    bar.addEventListener('touchmove', function (e) {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      var diff = currentY - startY;
      if (diff > 0) {
        var body = document.querySelector('.stack-drawer__body');
        if (body) body.style.transform = 'translateY(' + diff + 'px)';
      }
    }, { passive: true });

    bar.addEventListener('touchend', function () {
      if (!isDragging) return;
      isDragging = false;
      var diff = currentY - startY;
      var body = document.querySelector('.stack-drawer__body');

      drawer.style.transition = '';
      if (body) body.style.transform = '';

      if (diff > 60) {
        collapseDrawer();
      }
    });
  }

  /* ===== TOUCH SWIPE TO CLOSE SHEET ===== */
  function initSheetSwipe() {
    var handle = document.querySelector('.stack-sheet__handle');
    var sheet = document.getElementById('stack-sheet');
    if (!sheet || !handle) return;

    var startY = 0;
    var currentY = 0;
    var isDragging = false;

    handle.addEventListener('touchstart', function (e) {
      if (!sheetOpen) return;
      startY = e.touches[0].clientY;
      currentY = startY;
      isDragging = true;
      sheet.style.transition = 'none';
    }, { passive: true });

    handle.addEventListener('touchmove', function (e) {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      var diff = currentY - startY;
      if (diff > 0) {
        sheet.style.transform = 'translateY(' + diff + 'px)';
      }
    }, { passive: true });

    handle.addEventListener('touchend', function () {
      if (!isDragging) return;
      isDragging = false;
      var diff = currentY - startY;

      sheet.style.transition = '';
      sheet.style.transform = '';

      if (diff > 80) {
        closeSheet();
      }
    });
  }

  /* ===== INIT ===== */
  function init() {
    // Attach card click handlers
    var cards = document.querySelectorAll('.stack-card');
    for (var i = 0; i < cards.length; i++) {
      (function (card) {
        var btn = card.querySelector('.stack-card__add-btn');
        var handler = function (e) {
          e.preventDefault();
          e.stopPropagation();
          handleCardClick(card);
        };
        if (btn) {
          btn.addEventListener('click', handler);
        }
        card.addEventListener('click', function (e) {
          if (e.target.closest('.stack-card__add-btn')) return;
          e.preventDefault();
          handleCardClick(card);
        });
      })(cards[i]);
    }

    // Mobile drawer bar toggle
    var drawerBar = document.querySelector('.stack-drawer__bar');
    if (drawerBar) {
      drawerBar.addEventListener('click', toggleDrawer);
    }

    // Mobile drawer overlay close
    var overlay = getOverlay();
    if (overlay) {
      overlay.addEventListener('click', collapseDrawer);
    }

    // Mobile drawer checkout button
    var drawerCheckout = document.getElementById('stack-drawer-checkout');
    if (drawerCheckout) {
      drawerCheckout.addEventListener('click', function (e) {
        e.stopPropagation();
        handleCheckout();
      });
    }

    // Desktop summary checkout button
    var summaryCheckout = document.getElementById('stack-summary-checkout');
    if (summaryCheckout) {
      summaryCheckout.addEventListener('click', handleCheckout);
    }

    // Final CTA button
    var finalCTA = document.getElementById('stack-final-cta-btn');
    if (finalCTA) {
      finalCTA.addEventListener('click', function (e) {
        e.preventDefault();
        handleFinalCTA();
      });
    }

    // Sheet overlay close
    var sheetOverlay = document.getElementById('stack-sheet-overlay');
    if (sheetOverlay) {
      sheetOverlay.addEventListener('click', closeSheet);
    }

    // Init modules
    initSmoothScroll();
    initDrawerSwipe();
    initSheetSwipe();

    // Initial render
    onStateChange();
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
